/**
 * AuthProvider component and context
 *
 * Provides authentication state management using React Context API.
 * Manages user authentication state, login/logout operations, and error handling.
 */

import type { ReactNode, FC, ReactElement } from 'react';
import { createContext, useCallback, useState, useEffect } from 'react';
import type { AuthContextType, AuthState, LoginCredentials, User, UserUpdateData } from './types';
import {
  login as authServiceLogin,
  logout as authServiceLogout,
  validateToken,
} from './authService';
import { registerAuthFailureHandler } from './authHandlers';

export const AuthContext = createContext<AuthContextType | undefined>(undefined);

interface AuthProviderProps {
  children: ReactNode;
}

export const AuthProvider: FC<AuthProviderProps> = ({ children }): ReactElement => {
  const [state, setState] = useState<AuthState>({
    user: null,
    isAuthenticated: false,
    isLoading: true, // Start loading to check stored token
    error: null,
  });

  // Check for existing token on mount and register auth failure handler
  useEffect(() => {
    // Register handler for mid-session 401 failures (via fetchWithAuth)
    // Uses authServiceLogout + setState directly to avoid closure over logout callback
    registerAuthFailureHandler(() => {
      authServiceLogout();
      //This state update will trigger a re-render and redirect to login if the user is on a protected route
      setState({ user: null, isAuthenticated: false, isLoading: false, error: null });
    });

    const checkAuth = async () => {
      const user = await validateToken();
      setState({
        user,
        isAuthenticated: user !== null,
        isLoading: false,
        error: null,
      });
    };
    checkAuth();
  }, []);

  const login = useCallback(async (credentials: LoginCredentials) => {
    setState((prev: AuthState) => ({ ...prev, isLoading: true, error: null }));

    try {
      await authServiceLogin(credentials);
      // Fetch full user from /me so all fields (e.g. hasAccess) are present
      const user = await validateToken();
      setState({
        user,
        isAuthenticated: user !== null,
        isLoading: false,
        error: null,
      });
    } catch (err) {
      // Prefer the structured code from ApiError (e.g. AUTH_INVALID_CREDENTIALS)
      // so the UI can map it directly to an i18n key.
      // err is a plain object satisfying ApiError, not an Error instance.
      const apiError = err as { code?: string };
      const errorMessage = apiError?.code ?? (err instanceof Error ? err.message : 'Login failed');
      setState({
        user: null,
        isAuthenticated: false,
        isLoading: false,
        error: errorMessage,
      });
      throw err;
    }
  }, []);

  // Awaits the server logout so the session cookie is invalidated before state clears.
  // This prevents a page reload from re-authenticating via a still-valid cookie.
  const logout = useCallback(async () => {
    await authServiceLogout();
    setState({
      user: null,
      isAuthenticated: false,
      isLoading: false,
      error: null,
    });
  }, []);

  const clearError = useCallback(() => {
    setState((prev: AuthState) => ({ ...prev, error: null }));
  }, []);

  // Update user data in context (state-only, no API call)
  // API calls should be made by the component, then call this to sync state
  const updateUser = useCallback(async (data: UserUpdateData) => {
    setState((prev: AuthState) => {
      if (!prev.user) return prev;
      return {
        ...prev,
        user: { ...prev.user, ...data },
      };
    });
  }, []);

  // Sync auth context from an already-stored token (e.g. after registration)
  // Validates the token against the backend and updates context state
  const setAuthFromToken = useCallback(async (): Promise<User | null> => {
    setState((prev: AuthState) => ({ ...prev, isLoading: true }));
    const user = await validateToken();
    setState({
      user,
      isAuthenticated: user !== null,
      isLoading: false,
      error: null,
    });
    return user;
  }, []);

  const value: AuthContextType = {
    ...state,
    login,
    logout,
    clearError,
    updateUser,
    setAuthFromToken,
  };

  return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>;
};
